"use client";

import { ChangeEvent, DragEvent, useRef, useState } from "react";
import Image from "next/image";
import { File, Image as ImageIcon, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/components/ui/use-toast";

interface EventImageUploadProps {
  currentImage: string | null;
  onImageUpload: (url: string) => void;
  onImageDelete: () => void;
}

export function EventImageUpload({
  currentImage,
  onImageUpload,
  onImageDelete,
}: EventImageUploadProps) {
  const [uploadState, setUploadState] = useState<{
    file: File | null;
    progress: number;
    uploading: boolean;
  }>({
    file: null,
    progress: 0,
    uploading: false,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const validFileTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

  const handleFile = async (file: File | undefined) => {
    if (!file) return;

    if (validFileTypes.includes(file.type)) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "Error",
          description: "File size exceeds 10MB. Please choose a smaller image.",
          variant: "destructive",
        });
        return;
      }

      setUploadState({ file, progress: 0, uploading: true });

      try {
        const formData = new FormData();
        formData.append("file", file);

        // Simulating progress before the actual upload
        const progressInterval = setInterval(() => {
          setUploadState((prev) => {
            // Only increase progress to 90% during simulation
            const newProgress = Math.min(90, prev.progress + 10);
            return { ...prev, progress: newProgress };
          });
        }, 200);

        // Actual upload
        const response = await fetch("/api/upload", {
          method: "POST",
          body: formData,
        });

        clearInterval(progressInterval);

        if (!response.ok) throw new Error("Upload failed");

        const { url } = await response.json();

        // Set progress to 100% and complete the upload
        setUploadState((prev) => ({
          ...prev,
          progress: 100,
          uploading: false,
        }));
        onImageUpload(url);
      } catch (error) {
        console.error("Error uploading image:", error);
        setUploadState((prev) => ({ ...prev, uploading: false }));
        toast({
          title: "Error",
          description: "Failed to upload image. Please try again.",
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: "Error",
        description: "Please upload a JPG, PNG, or WebP image.",
        variant: "destructive",
      });
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleFile(event.target.files?.[0]);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    handleFile(event.dataTransfer.files?.[0]);
  };

  const resetFile = () => {
    setUploadState({ file: null, progress: 0, uploading: false });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const { file, progress, uploading } = uploadState;

  return (
    <div className="w-full">
      {!currentImage ? (
        <>
          <div
            className="border-input mt-2 flex justify-center rounded-md border border-dashed px-6 py-12"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <div>
              <ImageIcon
                className="text-muted-foreground mx-auto h-12 w-12"
                aria-hidden={true}
              />
              <div className="text-muted-foreground flex text-sm leading-6">
                <p>Drag and drop or</p>
                <label
                  htmlFor="image-upload"
                  className="text-primary relative cursor-pointer rounded-sm pl-1 font-medium hover:underline hover:underline-offset-4"
                >
                  <span>choose image</span>
                  <input
                    id="image-upload"
                    name="image-upload"
                    type="file"
                    className="sr-only"
                    accept=".jpg, .jpeg, .png, .webp"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                  />
                </label>
                <p className="pl-1">to upload</p>
              </div>
            </div>
          </div>

          <p className="text-muted-foreground mt-2 text-xs leading-5 sm:flex sm:items-center sm:justify-between">
            <span>Accepted file types: JPG, PNG or WebP files.</span>
            <span className="pl-1 sm:pl-0">Max. size: 10MB</span>
          </p>

          {file && (
            <Card className="bg-muted relative mt-8 gap-4 p-4">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-foreground absolute top-1 right-1 h-8 w-8"
                aria-label="Remove"
                onClick={resetFile}
              >
                <X className="h-5 w-5 shrink-0" aria-hidden={true} />
              </Button>

              <div className="flex items-center space-x-2.5">
                <span className="bg-background ring-border flex h-10 w-10 shrink-0 items-center justify-center rounded-sm shadow-sm ring-1 ring-inset">
                  <ImageIcon
                    className="text-foreground h-5 w-5"
                    aria-hidden={true}
                  />
                </span>
                <div>
                  <p className="text-foreground text-xs font-medium">
                    {file?.name}
                  </p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {file && formatFileSize(file.size)}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Progress value={progress} className="h-1.5" />
                <span className="text-muted-foreground text-xs">
                  {progress}%
                </span>
              </div>
            </Card>
          )}
        </>
      ) : (
        <Card className="bg-muted relative mt-2 p-4">
          <div className="flex flex-col gap-4">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground absolute top-1 right-1 z-10 h-8 w-8"
              aria-label="Remove"
              onClick={onImageDelete}
            >
              <X className="h-5 w-5 shrink-0" aria-hidden={true} />
            </Button>

            <div className="relative h-48 w-full overflow-hidden rounded-md">
              <Image
                src={currentImage}
                alt="Uploaded event image"
                fill
                className="rounded-md object-cover"
              />
            </div>

            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-xs">Event Image</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={onImageDelete}
              >
                Replace Image
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
